import { useMemo } from 'react';
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

type AxisDef = { key: string; label: string };

type CalibrationRadarChartProps = {
  title: string;
  axes: AxisDef[];
  live?: Record<string, number> | null;
  reference?: Record<string, number> | null;
  meanTag?: Record<string, number> | null;
  heightClassName?: string;
};

function toChartRows(
  axes: AxisDef[],
  live: Record<string, number> | null | undefined,
  reference: Record<string, number> | null | undefined,
  meanTag: Record<string, number> | null | undefined
) {
  return axes.map((axis) => ({
    axis: axis.label,
    key: axis.key,
    live: live ? Number((live[axis.key] ?? 0).toFixed(3)) : null,
    referenz: reference ? Number((reference[axis.key] ?? 0).toFixed(3)) : null,
    mittel: meanTag ? Number((meanTag[axis.key] ?? 0).toFixed(3)) : null
  }));
}

export function averageRadarRecords(
  snapshots: Array<{ radar: Record<string, number>; decisionRadar?: Record<string, number> }>,
  field: 'radar' | 'decisionRadar' = 'radar'
): Record<string, number> | null {
  if (!snapshots.length) return null;
  const first = field === 'radar' ? snapshots[0].radar : snapshots[0].decisionRadar;
  const keys = Object.keys(first ?? {});
  return Object.fromEntries(
    keys.map((key) => [
      key,
      snapshots.reduce((sum, snap) => {
        const record = field === 'radar' ? snap.radar : snap.decisionRadar;
        return sum + (record?.[key] ?? 0);
      }, 0) / snapshots.length
    ])
  );
}

export default function CalibrationRadarChart({
  title,
  axes,
  live,
  reference,
  meanTag,
  heightClassName = 'h-[min(360px,48vh)] min-h-[240px]'
}: CalibrationRadarChartProps) {
  const chartData = useMemo(() => toChartRows(axes, live, reference, meanTag), [axes, live, reference, meanTag]);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
      <div className={`w-full ${heightClassName}`}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="70%">
            <PolarGrid stroke="currentColor" className="text-gray-300 dark:text-gray-600" />
            <PolarAngleAxis
              dataKey="axis"
              tick={{ fill: 'currentColor', fontSize: 9 }}
              className="text-gray-600 dark:text-gray-300"
            />
            <PolarRadiusAxis angle={90} domain={[0, 1]} tick={{ fontSize: 9 }} tickCount={5} />
            <Tooltip
              formatter={(value: number, name: string) => [
                typeof value === 'number' ? value.toFixed(2) : '—',
                name
              ]}
              labelFormatter={(label) => label}
              contentStyle={{ fontSize: 12 }}
            />
            {meanTag ? (
              <Radar
                name="Mittel (Tag)"
                dataKey="mittel"
                stroke="#8b5cf6"
                fill="#8b5cf6"
                fillOpacity={0.1}
                strokeWidth={2}
                isAnimationActive={false}
              />
            ) : null}
            {reference ? (
              <Radar
                name="Referenz"
                dataKey="referenz"
                stroke="#f59e0b"
                fill="#f59e0b"
                fillOpacity={0.12}
                strokeWidth={2}
                strokeDasharray="4 4"
                isAnimationActive={false}
              />
            ) : null}
            {live ? (
              <Radar
                name="Live"
                dataKey="live"
                stroke="#0ea5e9"
                fill="#0ea5e9"
                fillOpacity={0.25}
                strokeWidth={2}
                isAnimationActive={false}
              />
            ) : null}
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
